import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateIGTagModalCloseState,
  updateIGTags
} from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import TagComponent from './tagComponent';
import { useTranslate } from '@tolgee/react';
import './tagger.css';
import { IMAGE, VIDEO } from '../../../config/constants';

interface Props {
  selectedFile: any;
  croppedImageUrl: string;
  saveBtnRef: React.MutableRefObject<any>;
  handleClose: Function;
  setStartSubmission: React.Dispatch<React.SetStateAction<boolean>>;
  startSubmission: boolean;
  setSelectedFile: React.Dispatch<any>;
  instagramUserTags: any;
  currentFileIndex: number;
  editorChanged: boolean;
  setEditorChanged: React.Dispatch<React.SetStateAction<boolean>>;
  submitRef: any;
}
interface Tag {
  x: number;
  y: number;
  username: string;
}
const Tagger: React.FC<Props> = ({
  selectedFile,
  croppedImageUrl,
  saveBtnRef,
  handleClose,
  setStartSubmission,
  startSubmission,
  setSelectedFile,
  currentFileIndex,
  instagramUserTags,
  editorChanged,
  setEditorChanged,
  submitRef
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [tags, setTags] = useState<Tag[]>(
    instagramUserTags?.find((obj) => obj?.uuid === selectedFile?.uuid)?.tags ||
      []
  );

  console.log('tags', tags);
  const [currentTag, setCurrentTag] = useState<Tag | null>(null);
  const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState('500px');

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef(null);

  const handleImageClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!editorChanged) {
      dispatch(
        updateIGTagModalCloseState({
          state: true
        })
      );
      setEditorChanged(true);
    }

    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const currentWidth = rect.width;
      const currentHeight = rect.height;

      // Calculate percentage positions
      const percentX = +(x / currentWidth).toFixed(4); // Round to 4 decimal places
      const percentY = +(y / currentHeight).toFixed(4);

      setCurrentTag({ x: percentX, y: percentY, username: '' });
      setEditingTagIndex(tags.length);
    }
  };

  const getRelativeX = (x: number) => {
    const rect = imageRef.current?.getBoundingClientRect();
    const currentWidth = rect?.width ?? 0;

    return x * currentWidth;
  };

  const getRelativeY = (y: number) => {
    const rect = imageRef.current?.getBoundingClientRect();
    const currentHeight = rect?.height ?? 0;

    return y * currentHeight;
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currentTag) {
      setCurrentTag({ ...currentTag, username: e.target.innerText.trim() });
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (currentTag && currentTag.username && tags?.length < 10) {
      const updatedTags = [...tags];
      if (editingTagIndex !== null && editingTagIndex < tags.length) {
        updatedTags[editingTagIndex] = currentTag;
      } else {
        updatedTags.push(currentTag);
      }

      setTags(updatedTags);
      setCurrentTag(null);
      setEditingTagIndex(null);
    }
  };

  const handleTagDelete = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleTagEdit = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setEditingTagIndex(index);
    setCurrentTag(tags[index]);
  };

  const getTaggablePreviewSrc = () => {
    if (selectedFile?.media_type === VIDEO) {
      return (
        selectedFile?.thumbnail_image ||
        selectedFile?.preview_image ||
        selectedFile?.assetBlob ||
        selectedFile?.original_asset ||
        ''
      );
    }

    return (
      croppedImageUrl ||
      selectedFile?.preview_image ||
      selectedFile?.assetBlob ||
      selectedFile?.original_asset ||
      ''
    );
  };

  useEffect(() => {
    setCurrentTag(null);
    setEditingTagIndex(null);
    setTags(
      instagramUserTags?.find((obj) => obj?.uuid === selectedFile?.uuid)
        ?.tags || []
    );
  }, [selectedFile, instagramUserTags]);

  const handleTagChange = (index: number, newText: string) => {
    setTags((prevTags) =>
      prevTags.map((tag, i) => (i === index ? { ...tag, text: newText } : tag))
    );
  };

  const handleSubmitTags = async () => {
    setStartSubmission(true);
    await dispatch(updateIGTags({ uuid: selectedFile?.uuid, newTags: tags }));
    dispatch(updateIGTagModalCloseState({ state: false }));
    setEditorChanged(false);
    setTimeout(() => {
      setStartSubmission(false);
    }, 1000);
  };

  useEffect(() => {
    const trigger = async () => {
      if (imageRef.current) {
        setImageLoaded(false);
        const img = new Image();
        img.src = getTaggablePreviewSrc();

        img.onload = () => {
          setImageLoaded(true);
        };
      }
    };

    trigger();
  }, [selectedFile, croppedImageUrl]);

  useEffect(() => {
    if (textRef.current) {
      textRef.current.focus();
    }
  }, [currentTag]);
  useEffect(() => {
    if (textRef.current) {
      const handlePlaceholder = () => {
        if (textRef.current && textRef.current.textContent === '') {
          textRef.current.classList.add('tagPlaceholder');
        } else {
          textRef.current.classList.remove('tagPlaceholder');
        }
      };
      handlePlaceholder();

      textRef.current.addEventListener('input', handlePlaceholder);
      return () => {
        if (textRef.current) {
          textRef.current.removeEventListener('input', handlePlaceholder);
        }
      };
    }
  }, [currentTag]);

  function updateHeight() {
    const elem = document.getElementById('container');
    const rect = elem.getBoundingClientRect();
    setContainerHeight(`${rect?.height}px`);
  }

  useEffect(() => {
    updateHeight(); // Initial call to set height

    const handleResize = () => {
      updateHeight();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      onClick={() => {
        setCurrentTag(null);
        setEditingTagIndex(null);
      }}
      style={{ minHeight: 0 }}
      className="w-100 h-100 flex-fill bg-white rounded-3 d-flex justify-content-center align-items-center overflow-hidden"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ overflowY: 'auto', minHeight: 0 }}
        id="container"
        className="mx-auto align-items-center justify-content-center d-flex flex-column gap-4 w-100 h-100"
      >
        {[IMAGE, VIDEO].includes(selectedFile?.media_type) ? (
          <div
            className="position-relative d-flex flex-column gap-2 justify-content-center align-items-center p-3"
            onClick={handleImageClick}
            ref={containerRef}
            style={{ zIndex: 1 }}
          >
            <img
              ref={imageRef}
              src={getTaggablePreviewSrc()}
              alt="Selected"
              style={{
                maxWidth: '100%',
                maxHeight: `calc(${containerHeight} - 30px)`,

                objectFit: 'contain'
              }}
            />

            {tags.map((tag, index) => (
              <div
                key={index + 'tagss'}
                className="position-absolute"
                style={{
                  left: getRelativeX(tag.x),
                  top: getRelativeY(tag.y),
                  zIndex: 3
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{ height: '24px' }}
                  className="d-flex align-items-center"
                >
                  <TagComponent
                    key={'tagkey' + index}
                    index={index}
                    text={tag.username}
                    onChange={handleTagChange}
                    handleTagDelete={handleTagDelete}
                    handleTagEdit={() => {
                      setCurrentTag(null);
                      setEditingTagIndex(null);
                    }}
                  />
                </div>
              </div>
            ))}
            {currentTag && (
              <div
                className="position-absolute"
                style={{
                  left: getRelativeX(currentTag.x),
                  top: getRelativeY(currentTag.y),
                  zIndex: 4
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  id="instagramUserTagBox"
                  className="d-flex border border-1 border-secondary align-items-center text-white bg-dark px-1 rounded me-2 instagramUserTagBox"
                  spellCheck={false}
                  contentEditable={false}
                  style={{ zIndex: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="min-w-100px"
                    id="collaboratorFieldText"
                    aria-placeholder={t('lomavisCreator.tagBox.placeholder')}
                    contentEditable="true"
                    ref={textRef}
                    onFocus={() => setIsFocused(true)}
                    onInput={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleInputBlur();
                      }
                    }}
                  ></div>
                  <i
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentTag(null);
                      setEditingTagIndex(null);
                    }}
                    style={{ zIndex: 5 }}
                    id="saveInstagramCollaboratorBtn"
                    className={`${
                      isFocused && currentTag.username
                        ? 'fa-sharp-duotone fa-solid fa-check text-success'
                        : 'fa-solid fa-xmark text-muted'
                    } icon-xs float-end closeBtn px-2 cursor-pointer`}
                  ></i>
                </div>{' '}
              </div>
            )}
          </div>
        ) : (
          <div className="position-relative d-flex flex-column gap-2 justify-content-center align-items-center p-3">
            {' '}
            <p className="h-500px w-500px bg-white d-flex justify-content-center align-items-center">
              {' '}
              <p className="text-dark">
                {t('lomavisCreator.IGtags.video_notAllowed')}
              </p>
            </p>{' '}
          </div>
        )}
      </div>
      <button
        onClick={handleSubmitTags}
        ref={submitRef}
        style={{ display: 'none' }}
      ></button>
    </div>
  );
};

export default Tagger;
